import React, {
  useState,
  useEffect,
  useCallback,
  useRef,
} from 'react';
import { useTranslation } from 'react-i18next';
// core
import {
  Button, IconButton, TextField, Tooltip,
  List, ListItem, ListItemText, Grid,
} from '@material-ui/core';

import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';

// icons
import DeleteIcon from '@material-ui/icons/Close';
import Filter2Icon from '@material-ui/icons/Filter2';

import { EDIT_MODE, VIEW_MODE } from '../../../utils/const/taskModes';
import { MODULES, FORMATS } from '../../../utils/const/quillModules';

import useStyles from '../styles';
import ExampleImageQuestion from '../../ExampleImageQuestion';

function WelcomePage({
  identifier, pages, info, handleDeleteQuestion, handleDuplicateQuestion,
  mode,
}) {
  const styles = useStyles();
  const { t } = useTranslation();

  const [open, setOpen] = useState(false);
  const [file, setFile] = useState({});
  const [image, setImage] = useState({});
  const [description, setDescription] = useState(
    info ? info.description : pages[identifier - 1].description,
  );

  const infoRef = useRef(info);
  infoRef.current = info;
  const pagesRef = useRef(pages);
  pagesRef.current = pages;
  const identifierRef = useRef(identifier);
  identifierRef.current = identifier;

  useEffect(() => {
    const currentInfo = infoRef.current || pagesRef.current[identifierRef.current - 1];

    if (currentInfo) {
      setImage((prevImage) => ({
        ...prevImage,
        url: currentInfo.imageUrl,
        name: currentInfo.imageKey,
      }));
    }
  }, []);

  const [localTitle, setLocalTitle] = useState(
    info ? info.title : pages[identifier - 1]?.title || '',
  );
  const [localShortQuestion, setLocalShortQuestion] = useState(
    info ? info.shortQuestion || '' : pages[identifier - 1]?.shortQuestion || '',
  );
  const timeoutRef = useRef(null);

  useEffect(() => {
    setLocalTitle(info ? info.title : pages[identifier - 1]?.title || '');
    setLocalShortQuestion(info ? info.shortQuestion || '' : pages[identifier - 1]?.shortQuestion || '');
  }, [info, pages, identifier]);

  const handleOnChange = useCallback((event) => {
    const { id, value, imageUrl } = event.target;

    if (id === 'title') {
      setLocalTitle(value);
    }

    if (id === 'shortQuestion') {
      setLocalShortQuestion(value);
    }

    if (timeoutRef.current) clearTimeout(timeoutRef.current);

    timeoutRef.current = setTimeout(() => {
      // eslint-disable-next-line no-param-reassign
      pages[identifier - 1] = {
        ...pages[identifier - 1],
        [id]: value,
        questionChoices: null,
        questionLogic: [{
          questionIdentifier: identifier,
          questionChoiceIdentifier: null,
          nextQuestionIdentifier: `${Number.parseInt(identifier, 10) + 1}`,
        }],
        imageUrl,
      };
    }, 400);
  }, [identifier, pages]);

  const handleBlur = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    if (localTitle !== undefined) {
      // eslint-disable-next-line no-param-reassign
      pages[identifier - 1].title = localTitle;
    }
  }, [localTitle, identifier, pages]);

  return (
    <div className={styles.root}>
      {/* <img src="/TCGHRES2021BACK2MainBlue.png" alt="TCG" /> */}
      <div className={styles.container}>
        <p className={styles.identifier}>{identifier}.</p>
        <div className={styles.questionContainer}>
          <TextField
            id="title"
            className={styles.welcomeTitle}
            placeholder={t('task.taskEdition.welcomePageText.title')}
            InputProps={{ disableUnderline: true }}
            inputProps={{ maxLength: 250 }}
            multiline
            value={localTitle}
            onChange={mode === EDIT_MODE ? handleOnChange : null}
            onBlur={handleBlur}
            disabled={mode === VIEW_MODE}
          />
          <TextField
            id="shortQuestion"
            className={styles.question}
            placeholder={mode !== VIEW_MODE ? t('task.taskEdition.shortQuestionPlaceholder') : ''}
            InputProps={{ disableUnderline: true }}
            inputProps={{ maxLength: 250 }}
            multiline
            value={localShortQuestion}
            onChange={mode === EDIT_MODE ? handleOnChange : null}
            disabled={mode === VIEW_MODE}
          />
          <ReactQuill
            className={styles.reactQuill}
            modules={MODULES}
            formats={FORMATS}
            value={description}
            readOnly={mode === VIEW_MODE}
            placeholder={t('task.taskEdition.welcomePageText.message')}
            onChange={
              mode === EDIT_MODE
                ? (value) => {
                  setDescription(value);
                  handleOnChange({
                    target: {
                      id: 'description',
                      name: 'description',
                      value,
                    },
                  });
                }
                : null}
          />
          {mode !== VIEW_MODE ? (
            <>
              <Button
                onClick={() => setOpen(true)}
              >
                {t('task.taskForm.addMediaTitle')}
              </Button>
            </>
          ) : null
          }
          {image.url
            ? <>
              <Grid container>
                <Grid item xs={1}>
                  <Tooltip
                    title={
                      <img
                        alt='reference-image'
                        src={image.url}
                        width='100%'
                        height='100%'
                      />
                    }
                    arrow
                    placement='top-end'
                  >
                    <img
                      alt='reference-image'
                      src={image.url}
                      width='50px'
                      height='50px'
                    />
                  </Tooltip>
                </Grid>
                <Grid item xs={5}>
                  <List className={styles.list}>
                    <ListItem>
                      <ListItemText
                        primary={image.name}
                        primaryTypographyProps={{ variant: 'h5' }}
                        secondary={image.size ? `  (${image.size})` : null}
                      />
                    </ListItem>
                  </List>
                </Grid>
              </Grid>
            </>
            : null
          }
        </div>
      </div>
      {mode !== VIEW_MODE ? (
        <>
          <IconButton
            id={identifier}
            onClick={handleDuplicateQuestion}
            className={styles.deleteButton}
          >
            <Tooltip title={t('task.taskEdition.duplicate')}>
              <Filter2Icon />
            </Tooltip>
          </IconButton>
          <IconButton
            id={identifier}
            onClick={handleDeleteQuestion}
            className={styles.deleteButton}
          >
            <DeleteIcon />
          </IconButton>
        </>
      ) : null}
      <ExampleImageQuestion
        open={open}
        setOpen={setOpen}
        file={file}
        setFile={setFile}
        handleOnChange={handleOnChange}
        setImage={setImage}
        image={image}
      />
    </div>
  );
}

export default WelcomePage;
